/**
 * Oudh Al Anfar — shared UI behaviours
 * ---------------------------------------------------------------------------
 * Five small custom elements, no framework, no dependencies beyond Horizon's
 * own `Component` base class:
 *
 *   <oaa-tabs>           Shop By, and the Bestsellers / New / Back in stock rails
 *   <oaa-rail>           previous / next controls for a scroll-snap row
 *   <oaa-house-switcher> hovering a house swaps the strip below it
 *   <oaa-sticky-atc>     the mobile bar, shown once the in-page button leaves
 *   <oaa-wishlist-toggle> a per-visitor wishlist held in localStorage
 *
 * Everything degrades: without JavaScript the tabs render as stacked panels,
 * the rail is still swipeable, and the wishlist button simply does nothing.
 * ---------------------------------------------------------------------------
 */

/* ===========================================================================
 * <oaa-tabs>
 * Full APG tablist semantics: roving tabindex, arrow keys, Home / End.
 * ======================================================================== */
class OaaTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.panels = [...this.querySelectorAll('[role="tabpanel"]')];
    if (!this.tabs.length) return;

    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);

    // Establish the initial state from markup so the server-rendered panel
    // is the one that stays visible — no flash, no layout shift.
    const initial = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    this.#select(initial < 0 ? 0 : initial, { focus: false });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeydown);
  }

  #onClick = (event) => {
    const tab = event.target.closest('[role="tab"]');
    if (!tab || !this.tabs.includes(tab)) return;
    this.#select(this.tabs.indexOf(tab), { focus: false });
  };

  #onKeydown = (event) => {
    const tab = event.target.closest('[role="tab"]');
    if (!tab || !this.tabs.includes(tab)) return;

    const current = this.tabs.indexOf(tab);
    const isRtl = getComputedStyle(this).direction === 'rtl';
    let next = null;

    switch (event.key) {
      case 'ArrowRight':
        next = isRtl ? current - 1 : current + 1;
        break;
      case 'ArrowLeft':
        next = isRtl ? current + 1 : current - 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = this.tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#select((next + this.tabs.length) % this.tabs.length, { focus: true });
  };

  #select(index, { focus }) {
    this.tabs.forEach((tab, i) => {
      const selected = i === index;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
    });

    this.panels.forEach((panel, i) => {
      panel.hidden = i !== index;
    });

    if (focus) this.tabs[index]?.focus();

    // Rails inside a newly revealed panel need to re-measure their overflow.
    this.querySelectorAll('oaa-rail').forEach((rail) => rail.refresh?.());
  }
}

/* ===========================================================================
 * <oaa-rail>
 * Scroll-snap row with previous / next controls. The buttons disable
 * themselves at each end so a keyboard user is never dropped into a no-op.
 * ======================================================================== */
class OaaRail extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-rail-track]');
    this.prev = this.querySelector('[data-rail-prev]');
    this.next = this.querySelector('[data-rail-next]');
    if (!this.track) return;

    this.prev?.addEventListener('click', () => this.#scrollBy(-1));
    this.next?.addEventListener('click', () => this.#scrollBy(1));
    this.track.addEventListener('scroll', this.#onScroll, { passive: true });

    this.#observer = new ResizeObserver(() => this.refresh());
    this.#observer.observe(this.track);
    this.refresh();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }

  #observer;

  #onScroll = () => {
    // rAF-throttled: scroll fires far more often than the buttons can change.
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = null;
      this.refresh();
    });
  };

  #frame = null;

  #scrollBy(direction) {
    const item = this.track.firstElementChild;
    const step = item ? item.getBoundingClientRect().width + 24 : this.track.clientWidth * 0.8;
    const isRtl = getComputedStyle(this).direction === 'rtl';
    this.track.scrollBy({ left: step * direction * (isRtl ? -1 : 1), behavior: 'smooth' });
  }

  refresh() {
    if (!this.track) return;
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    const max = scrollWidth - clientWidth;
    const position = Math.abs(scrollLeft);
    const overflowing = max > 2;

    this.toggleAttribute('data-overflowing', overflowing);
    if (this.prev) this.prev.disabled = !overflowing || position <= 2;
    if (this.next) this.next.disabled = !overflowing || position >= max - 2;
  }
}

/* ===========================================================================
 * <oaa-house-switcher>
 * Hovering (or focusing, or tapping) a house swaps the product strip below to
 * that house's top five. Pointer and keyboard both work; touch falls back to
 * the click, which is why the trigger is a real button.
 * ======================================================================== */
class OaaHouseSwitcher extends HTMLElement {
  connectedCallback() {
    this.triggers = [...this.querySelectorAll('[data-house-trigger]')];
    this.strips = [...this.querySelectorAll('[data-house-strip]')];
    if (!this.triggers.length) return;

    this.triggers.forEach((trigger) => {
      trigger.addEventListener('pointerenter', () => this.#show(trigger.dataset.houseTrigger));
      trigger.addEventListener('focus', () => this.#show(trigger.dataset.houseTrigger));
      trigger.addEventListener('click', () => this.#show(trigger.dataset.houseTrigger));
    });
  }

  #show(handle) {
    if (!handle || handle === this.#active) return;
    this.#active = handle;

    this.strips.forEach((strip) => {
      strip.hidden = strip.dataset.houseStrip !== handle;
    });

    this.triggers.forEach((trigger) => {
      trigger.setAttribute('aria-current', trigger.dataset.houseTrigger === handle ? 'true' : 'false');
    });
  }

  #active = null;
}

/* ===========================================================================
 * <oaa-sticky-atc>
 * Shows the mobile bar only once the in-page Add to Cart leaves the viewport.
 * An IntersectionObserver, not a scroll listener — no work on the main thread
 * between crossings.
 * ======================================================================== */
class OaaStickyAtc extends HTMLElement {
  connectedCallback() {
    const targetSelector = this.dataset.watch;
    const target = targetSelector ? document.querySelector(targetSelector) : null;

    if (!target) {
      // Nothing to watch means nothing to hide behind: leave the bar hidden
      // rather than pinning a duplicate button over the page.
      return;
    }

    this.#observer = new IntersectionObserver(
      ([entry]) => {
        this.dataset.visible = String(!entry.isIntersecting);
      },
      { rootMargin: '0px 0px -8px 0px' }
    );

    this.#observer.observe(target);
    this.querySelector('[data-sticky-proxy]')?.addEventListener('click', () => target.click());
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }

  #observer;
}

/* ===========================================================================
 * <oaa-wishlist-toggle>
 * A per-visitor wishlist. Held in localStorage, which means it is private to
 * one browser and can come back empty — so every read and write is guarded
 * and the button renders correctly with no stored value.
 * ======================================================================== */
const WISHLIST_KEY = 'oaa:wishlist';

function readWishlist() {
  try {
    const raw = localStorage.getItem(WISHLIST_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
}

function writeWishlist(set) {
  try {
    localStorage.setItem(WISHLIST_KEY, JSON.stringify([...set]));
  } catch {
    /* Private windows and blocked site data both land here. Non-fatal. */
  }
}

class OaaWishlistToggle extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    this.handle = this.dataset.handle;
    if (!this.button || !this.handle) return;

    this.#sync();
    this.button.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.#onClick);
  }

  #onClick = (event) => {
    event.preventDefault();
    const list = readWishlist();
    if (list.has(this.handle)) list.delete(this.handle);
    else list.add(this.handle);
    writeWishlist(list);
    this.#sync();
    document.dispatchEvent(new CustomEvent('oaa:wishlist:change', { detail: { size: list.size } }));
  };

  #sync() {
    const saved = readWishlist().has(this.handle);
    this.button.setAttribute('aria-pressed', String(saved));
    const label = saved ? this.dataset.labelRemove : this.dataset.labelAdd;
    if (label) this.button.setAttribute('aria-label', label);
  }
}

/* ===========================================================================
 * Registration
 * `define` is guarded so a section rendered twice by the Theme Editor cannot
 * throw on re-definition.
 * ======================================================================== */
const registry = {
  'oaa-tabs': OaaTabs,
  'oaa-rail': OaaRail,
  'oaa-house-switcher': OaaHouseSwitcher,
  'oaa-sticky-atc': OaaStickyAtc,
  'oaa-wishlist-toggle': OaaWishlistToggle,
};

for (const [tag, constructor] of Object.entries(registry)) {
  if (!customElements.get(tag)) customElements.define(tag, constructor);
}
